// Main Content Component
import React, { useEffect, useState } from "react";
import { MessageCircle, FileText, Search } from "lucide-react";
import CommentTab from "../../components/admin/CommentTab";
import PostsTab from "../../components/admin/PostTab";
import { useDebounce } from "../../hooks/useDebounce";

// Sidebar Component
const Sidebar = ({ activeSection, setActiveSection }) => {
  const tutiesItems = [
    { icon: MessageCircle, label: "Comments", key: "comments" },
    { icon: FileText, label: "Posts", key: "posts" },
  ];

  return (
    <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto h-screen">
      <div className="p-4">
        <div>
          {tutiesItems.map((item) => (
            <div
              key={item.key}
              className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                activeSection === item.key
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              onClick={() => setActiveSection(item.key)}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Filter Bar Component
const FilterBar = ({
  activeSection,
  searchTerm,
  setSearchTerm,
  isDeleted,
  setIsDeleted,
}) => {
  const getPlaceholder = () => {
    return activeSection === "comments"
      ? "Search comments..."
      : "Search posts...";
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder={getPlaceholder()}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Status</span>
        <select
          value={isDeleted}
          onChange={(e) =>
            setIsDeleted(e.target.value as "published" | "deleted")
          }
          className="border border-gray-300 rounded px-3 py-1 bg-white text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="published">Published</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>
    </div>
  );
};

// Main Content Component
const MainContent = ({ activeSection }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDeleted, setIsDeleted] = useState<"published" | "deleted">(
    "published"
  );
  const debounceSearch = useDebounce(searchTerm, 200);

  useEffect(() => {
    setSearchTerm("");
    setIsDeleted("published");
  }, [activeSection]);

  return (
    <div className="flex-1 bg-gray-50 flex flex-col min-h-screen">
      <FilterBar
        activeSection={activeSection}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        isDeleted={isDeleted}
        setIsDeleted={setIsDeleted}
      />

      <div className="p-6 flex-1">
        {activeSection === "comments" && (
          <CommentTab
            search={debounceSearch}
            isDeleted={isDeleted === "deleted"}
          />
        )}
        {activeSection === "posts" && (
          <PostsTab
            search={debounceSearch}
            isDeleted={isDeleted === "deleted"}
          />
        )}
      </div>
    </div>
  );
};

// Main App Component
const AdminPanel = () => {
  const [activeSection, setActiveSection] = useState("comments");

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
      <MainContent activeSection={activeSection} />
    </div>
  );
};

export default AdminPanel;
